import { wrapFetch } from '../libs/fetch';
import type { ResponseFetchCompanyPlanName } from '../types/personShow';
import type { ResponseSelectedStatuses } from '../types/entries';
import type { NotificationsAndUnreads, MarkReadAll, PlanInfo } from '../types/companyHeader';
import type {
  SearchRecruitments,
  SearchFormOptionsForRecruitments,
  FilterdCompanies,
  CompanyIds,
  SortCompanies,
  CompanyData,
  ProcessingSelectionStatusesResponse,
  SelectedStatusesResponse,
  SaveSelectionStatusesRequest,
  SaveSelectionStatusesResponse,
  CheckApplicantsExistedIdsRequest,
  ApplicantsExistedIdsResponse,
  SaveEmailRequest
} from '../types/companies';
import type { SaveResponse } from '../types/companyChangeEmail';
import type {
  CompanyProfile, CompanyRecruitmentIds, CompanyRecruitmentDetail,
  CompanyRecruitmentEmploymentIds, CompanyBrandIds, CompanyEventIds, CompanyEventDetail,
  CompanyPosts, CompanyPostDetail, CompanyTvs, CompanyTvDetail, CompanyProfileDetail,
  CompanyPeople
} from '../types/companyDetail';
import { PostResponse } from '../types/companyRegisterForm';

export const fetchNotifications = async () => {
  return await wrapFetch<NotificationsAndUnreads>('/api/v1/companies/get_notifications');
};

export const fetchMarkReadAll = async () => {
  return await wrapFetch<MarkReadAll>('/api/v1/companies/mark_read_all');
};

export const fetchPlanInfo = async () => {
  return await wrapFetch<PlanInfo>('/api/v1/companies/get_plan_info');
};

export const fetchCompanyPlanName = async () => {
  return await wrapFetch<ResponseFetchCompanyPlanName>('/api/v1/companies/get_company_plan_name');
};

export const searchRecruitments = async ({ csrfToken, data }: { csrfToken: string; data: { status?: string; [key: string]: any; } }) => {
  return await wrapFetch<SearchRecruitments>('/api/v1/companies/search_recruitments/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(data),
  });
};

export const getSearchCompanyFormOptiosForRecruitment = async (companyId: number | string, status: 'released' | 'stopped' = 'released') => {
  return await wrapFetch<SearchFormOptionsForRecruitments>(
    `/api/v1/companies/get_search_form_options_for_recruitments?id=${companyId}&status=${status}`
  );
};

export const getSelectedStatusName = async () => {
  return await wrapFetch<ResponseSelectedStatuses>('/api/v1/companies/get_selected_status_name');
};

export const getProcessingSelectionStatuses = async (): Promise<ProcessingSelectionStatusesResponse> => {
  return await wrapFetch<ProcessingSelectionStatusesResponse>('/api/v1/companies/get_processing_selection_statuses');
};

export const getSelectedStatuses = async (): Promise<SelectedStatusesResponse> => {
  return await wrapFetch<SelectedStatusesResponse>('/api/v1/companies/get_selected_statuses');
};

export const saveSelectionStatuses = async (data: SaveSelectionStatusesRequest, csrfToken: string): Promise<SaveSelectionStatusesResponse> => {
  return await wrapFetch<SaveSelectionStatusesResponse>('/api/v1/companies/save_selection_statuses', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify(data),
    credentials: 'same-origin'
  });
};

export const checkApplicantsExistedIds = async (data: CheckApplicantsExistedIdsRequest, csrfToken: string): Promise<ApplicantsExistedIdsResponse> => {
  return await wrapFetch<ApplicantsExistedIdsResponse>('/api/v1/companies/check_applicants_existed_ids', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify(data),
    credentials: 'same-origin'
  });
};

export const getFilteredCompanies = async ({ csrfToken, body }: { csrfToken: string; body: any }) => {
  return await wrapFetch<FilterdCompanies>('/api/v1/companies/filter', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
  });
};

export const getCompanyIds = async () => {
  return await wrapFetch<CompanyIds>('/api/v1/companies/fetch_company_ids');
};

export const sortCompanies = async ({ csrfToken, body }: { csrfToken: string; body: any }) => {
  return await wrapFetch<SortCompanies>('/api/v1/companies/sort_companies', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
  });
};

export const getShowCompany = async (companyId: number | string) => {
  return await wrapFetch<CompanyData>(`/api/v1/companies/show_company/?id=${companyId}`);
};

// 新しいメールアドレスへの確認メール送信をリクエストする(即時にはメールアドレスを変更しない)。
export const requestEmailChange = async (body: SaveEmailRequest, csrfToken: string) => {
  return await wrapFetch<SaveResponse>('/api/v1/companies/save_email', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
};

export const getCompanyProfile = async (companyId: number | string) => {
  return await wrapFetch<CompanyProfile>(`/api/v1/companies/get_company_profile?id=${companyId}`);
};

export const getCompanyProfileDetails = async (companyId: number | string) => {
  return await wrapFetch<CompanyProfileDetail>(`/api/v1/companies/get_company_profile_details?id=${companyId}`);
};

export const getCompanyRecruitments = async (companyId: number | string) => {
  return await wrapFetch<CompanyRecruitmentIds>(`/api/v1/companies/get_company_recruitments?id=${companyId}`);
};

export const getCompanyRecruitmentDetail = async (companyId: number | string) => {
  return await wrapFetch<CompanyRecruitmentDetail>(`/api/v1/companies/get_company_recruitment_details?id=${companyId}`);
};

export const getCompanyEmployments = async (companyId: number | string) => {
  return await wrapFetch<CompanyRecruitmentEmploymentIds>(`/api/v1/companies/get_company_employments?id=${companyId}`);
};

export const getCompanyBrands = async (companyId: number | string) => {
  return await wrapFetch<CompanyBrandIds>(`/api/v1/companies/get_company_brands?id=${companyId}`);
};

export const getCompanyEvents = async (companyId: number | string) => {
  return await wrapFetch<CompanyEventIds>(`/api/v1/companies/get_company_events?id=${companyId}`);
};

export const getCompanyEventDetails = async (companyId: number | string) => {
  return await wrapFetch<CompanyEventDetail>(`/api/v1/companies/get_company_event_details?id=${companyId}`);
};

export const getCompanyPosts = async (companyId: number | string) => {
  return await wrapFetch<CompanyPosts>(`/api/v1/companies/get_company_posts?id=${companyId}`);
};

export const getCompanyPostDetails = async (companyId: number | string) => {
  return await wrapFetch<CompanyPostDetail>(`/api/v1/companies/get_company_post_details?id=${companyId}`);
};

export const getCompanyTvs = async (companyId: number | string) => {
  return await wrapFetch<CompanyTvs>(`/api/v1/companies/get_company_tvs?id=${companyId}`);
};

export const getCompanyTvDetails = async (companyId: number | string) => {
  return await wrapFetch<CompanyTvDetail>(`/api/v1/companies/get_company_tv_details?id=${companyId}`);
};

export const getCompanyPeople = async (companyId: number | string) => {
  return await wrapFetch<CompanyPeople>(`/api/v1/companies/get_company_people?id=${companyId}`);
};

export const postNewPlan = async (data: FormData, csrfToken: string) => {
  return await wrapFetch<PostResponse>('/api/v1/companies/new_plan', {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken
    },
    body: data,
    credentials: 'same-origin'
  });
};

export const createPlanAndCompany = async (data: FormData, csrfToken: string) => {
  return await wrapFetch<PostResponse>('/api/v1/companies/create_plan_and_company', {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken
    },
    body: data,
    credentials: 'same-origin'
  });
};
